import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  Grid,
  RadioGroup,
  Radio,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getDealerDetails,
  updateDealerDetails,
  deleteDealer,
  dealersActions,
} from "store/slice/dealersSlice";
import { getGames } from "store/slice/gamesSlice";
import { getStudios } from "store/slice/studiosSlice";
import { useTranslation } from "react-i18next";

const FIELD_SIZE = "small";

const validationSchema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  name: Yup.string().max(255, "Name must be 255 characters or less").required("Name is required"),
  nickname: Yup.string().max(255, "Name must be 255 characters or less").required("Nickname is required"),
  phone: Yup.string().required("Phone is required"),
  quality: Yup.string().required().label("Quality"),
  canTakePartTime: Yup.boolean(),
  partTimeAvailability: Yup.string().nullable(),
  partTimePart: Yup.string()
    .nullable()
    .when("shift", {
      is: "4",
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    })
    .label("Shift part time part"),
  shift: Yup.string().required("Shift is required"),
  unavailable: Yup.boolean(),
  reserve: Yup.boolean(),
  workdays: Yup.number().min(1).max(7).required("Workdays are required"),
  restDays: Yup.number().min(1).max(7).required("Rest days are required"),
  gender: Yup.string().oneOf(["male", "female"], "Gender must be male or female").required("Gender is required"),
});

const shiftOptions = [
  { label: "Morning", value: 1 },
  { label: "Night", value: 2 },
  { label: "Full", value: 60 },
  { label: "Part-time", value: 4 },
];

const shiftPartOptions = [
  { label: "Mroning First part", value: 1 },
  { label: "Mroning Second part", value: 2 },
  { label: "Night First part", value: 3 },
  { label: "Night Second part", value: 4 },
];

const quality = [
  { label: "Good", value: 1 },
  { label: "Normal", value: 2 },
  { label: "Bad", value: 3 },
];

const weekDays = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
  { label: "Sunday", value: 0 },
];

const partTimeSlots = [
  { label: "Morning First", value: "morning_first" },
  { label: "Morning Second", value: "morning_second" },
  { label: "Morning Any", value: "morning_any" },
  { label: "Night First", value: "night_first" },
  { label: "Night Second", value: "night_second" },
  { label: "Night Any", value: "night_any" },
  { label: "Any", value: "any" },
];

function DealersEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dealerId } = useParams();
  const { t } = useTranslation();

  const store = useSelector((s) => s.dealers);
  const gamesStore = useSelector((s) => s.games);
  const studiosStore = useSelector((s) => s.studios);

  const [successMessage, setSuccessMessage] = useState("");

  // useEffect(() => {
  //   if (store.success.status || store.error.status) {
  //     const t = setTimeout(() => {
  //      dispatch(dealersActions.reset());
  //     }, 2000);
  //     return () => clearTimeout(t);
  //   }
  // }, [store.success.status, store.error.status, dispatch]);

  useEffect(() => {
    if (dealerId === "new") {
      dispatch(dealersActions.reset());
    }

    return () => {
      dispatch(dealersActions.reset());
    }
  }, [dealerId, dispatch]);

  useEffect(() => {
    if (dealerId) {
      dispatch(getDealerDetails(dealerId));
      dispatch(getGames());
      dispatch(getStudios());
    }
  }, [dealerId, dispatch]);

  const details = store.dealerDetails;

  useEffect(() => {
    if (dealerId === "new" && details?.id) {
      navigate(`/dealers/${details.id}`, { replace: true });
    }
  }, [details?.id, dealerId, navigate]);

  const initialValues = {
    email: details?.email || "",
    name: details?.name || "",
    nickname: details?.nickname || "",
    phone: details?.phone || "",
    shift: details?.shift || 60,
    canTakePartTime: details?.canTakePartTime || false,
    partTimeAvailability: details?.partTimeAvailability || "any",
    quality: details?.quality || 2,
    partTimePart: details?.partTimePart || 1,
    unavailable: Boolean(details?.unavailable) || false,
    reserve: Boolean(details?.reserve) || false,
    workdays: details?.workdays || 4,
    restDays: details?.restDays || 2,
    games: details?.games?.map((g) => g.id) || [],
    studios: details?.studios?.map((st) => st.id) || [],
    gender: details?.gender || "female",
    excludedWeekdays: details?.excludedWeekdays?.map(Number) || [],
  };

  const studiosOptions = studiosStore?.studios?.map((st) => ({ label: st.name, value: st.id })) || [];
  const gamesOptions = gamesStore?.games?.map((g) => ({ label: g.name, value: g.id })) || [];

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this dealer?");
    if (confirmDelete) {
      await dispatch(deleteDealer(dealerId));
      navigate("/dealers");
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const payload = { ...values, unavailable: +values.unavailable };
    await dispatch(updateDealerDetails({ dealerId, updatedData: payload }));
    setSubmitting(false);
    setSuccessMessage("Dealer details updated successfully!");
  };

  return (
    <DashboardLayout>
      <Typography variant="h4" gutterBottom>
        Edit Dealer Details
      </Typography>

      {store.success.status && (
        <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {store.error.status && (
        <Alert severity="error" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
          {store.error.message}
        </Alert>
      )}

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
        {({ values, handleChange, setFieldValue, handleBlur, touched, errors, isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Typography variant="body2">Gender</Typography>
                <RadioGroup row name="gender" value={values.gender} onChange={handleChange}>
                  <FormControlLabel value="male" control={<Radio size="small" />} label="Male" />
                  <FormControlLabel value="female" control={<Radio size="small" />} label="Female" />
                </RadioGroup>
                {touched.gender && errors.gender && <Typography color="error">{errors.gender}</Typography>}
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  label="Nickname"
                  name="nickname"
                  value={values.nickname}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nickname && Boolean(errors.nickname)}
                  helperText={touched.nickname && errors.nickname}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 12}}>
                <Autocomplete
                  options={quality}
                  getOptionLabel={(o) => o.label}
                  value={quality.find((o) => o.value === values.quality) || null}
                  onChange={(e, v) => setFieldValue("quality", v ? v.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size={FIELD_SIZE}
                      label="Quality"
                      variant="outlined"
                      error={touched.quality && Boolean(errors.quality)}
                      helperText={touched.quality && errors.quality}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Autocomplete
                  options={shiftOptions}
                  getOptionLabel={(o) => o.label}
                  value={shiftOptions.find((o) => o.value === values.shift) || null}
                  onChange={(e, v) => setFieldValue("shift", v ? v.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size={FIELD_SIZE}
                      label="Shift"
                      variant="outlined"
                      error={touched.shift && Boolean(errors.shift)}
                      helperText={touched.shift && errors.shift}
                    />
                  )}
                />
              </Grid>

              {values.shift == 4 ? (
                <Grid item size={{ xs: 12 }}>
                  <Autocomplete
                    options={shiftPartOptions}
                    getOptionLabel={(o) => o.label}
                    value={shiftPartOptions.find((o) => o.value === values.partTimePart) || null}
                    onChange={(e, v) => setFieldValue("partTimePart", v ? v.value : "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size={FIELD_SIZE}
                        label="Part"
                        variant="outlined"
                        error={touched.partTimePart && Boolean(errors.partTimePart)}
                        helperText={touched.partTimePart && errors.partTimePart}
                      />
                    )}
                  />
                </Grid>
              ) : null}

              <Grid item size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Checkbox size="small" name="canTakePartTime" checked={values.canTakePartTime} onChange={handleChange} />}
                  label="Can Take Part-Time"
                />
              </Grid>

              {values.canTakePartTime && (
                <Grid item size={{ xs: 12 }}>
                  <Autocomplete
                    options={partTimeSlots}
                    getOptionLabel={(o) => o.label}
                    value={partTimeSlots.find((o) => o.value === values.partTimeAvailability) || null}
                    onChange={(e, v) => setFieldValue("partTimeAvailability", v ? v.value : "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size={FIELD_SIZE}
                        label="Preferred Part-Time Slot"
                        variant="outlined"
                        error={touched.partTimeAvailability && Boolean(errors.partTimeAvailability)}
                        helperText={touched.partTimeAvailability && errors.partTimeAvailability}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Checkbox size="small" name="unavailable" checked={Boolean(values.unavailable)} onChange={handleChange} />}
                  label="Unavailable"
                />
              </Grid>
              
              <Grid item size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Checkbox size="small" name="reserve" checked={Boolean(values.reserve)} onChange={handleChange} />}
                  label={t('reserve')}
                />
              </Grid>

              <Grid item size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  type="number"
                  label="Workdays"
                  name="workdays"
                  value={values.workdays}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.workdays && Boolean(errors.workdays)}
                  helperText={touched.workdays && errors.workdays}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 6}}>
                <TextField
                  fullWidth
                  size={FIELD_SIZE}
                  type="number"
                  label="Rest Days"
                  name="restDays"
                  value={values.restDays}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.restDays && Boolean(errors.restDays)}
                  helperText={touched.restDays && errors.restDays}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={weekDays}
                  getOptionLabel={(o) => o.label}
                  value={weekDays
                    .filter((d) => values.excludedWeekdays.includes(d.value))
                    .map((d) => ({ label: d.label, value: d.value }))}
                  onChange={(e, nv) => setFieldValue("excludedWeekdays", nv.map((d) => d.value))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip key={option.value} label={option.label} size="small" {...getTagProps({ index })} />)
                  }
                  renderInput={(params) => (
                    <TextField {...params} size={FIELD_SIZE} variant="outlined" label="Exclude weekdays" placeholder="Exclude weekdays" />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={studiosOptions}
                  getOptionLabel={(o) => o.label}
                  value={studiosOptions.filter((st) => values.studios.includes(st.value))}
                  onChange={(e, nv) => setFieldValue("studios", nv.map((st) => st.value))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip key={option.value} label={option.label} size="small" {...getTagProps({ index })} />)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size={FIELD_SIZE}
                      variant="outlined"
                      label="Select Studio"
                      placeholder="Choose Studios"
                      error={touched.studios && Boolean(errors.studios)}
                      helperText={touched.studios && errors.studios}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={gamesOptions}
                  getOptionLabel={(o) => o.label}
                  value={gamesOptions.filter((g) => values.games.includes(g.value))}
                  onChange={(e, nv) => setFieldValue("games", nv.map((g) => g.value))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip key={option.value} label={option.label} size="small" {...getTagProps({ index })} />)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size={FIELD_SIZE}
                      variant="outlined"
                      label="Select Games"
                      placeholder="Choose games"
                      error={touched.games && Boolean(errors.games)}
                      helperText={touched.games && errors.games}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Button type="submit" size={FIELD_SIZE} variant="contained" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>

                <Button
                  size={FIELD_SIZE}
                  variant="text"
                  color="error"
                  sx={{ ml: 1 }}
                  onClick={handleDelete}
                >
                  Delete Dealer
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </DashboardLayout>
  );
}

export default DealersEdit;
